import { callWithNuxt } from '#app';
import type { Router } from '#vue-router';
import ky from 'ky';
import type { Pinia } from 'pinia';
import type { UserDto } from '~/dto/user';
import { useAuthStore } from "~/store/auth";
import { useCartStore } from "~/store/cart";
import type { ApiPdg } from "./api-pdg";

export default defineNuxtPlugin(function (nuxtApp) {
  // Doesn't persist: https://github.com/prazdevs/pinia-plugin-persistedstate/issues/190
  const { $pinia, $router } = nuxtApp;
  const authStore = useAuthStore($pinia as Pinia);
  const cartStore = useCartStore($pinia as Pinia);

  const $apiHttp = ky.extend({
    prefixUrl: nuxtApp.$config.public.apiUrl,
    hooks: {
      beforeRequest: [
        (request) => {
          if (nuxtApp.$config.public.debug) {
            console.info(`➡️ [${request.method}] ${request.url} ⏳`);
          }

          // - Si la requête est faite côté serveur, on transfert les headers de la requête
          if (import.meta.server) {
            const headers = nuxtApp.ssrContext?.event.node.req.headers;
            if (headers) {
              for (const key in headers) {
                request.headers.set(key, headers[key] as string);
              }
            }
          }

          // TODO: only use the token for custom endpoints
          if (authStore.token) {
            // console.debug(`Current refresh token: ${authStore.refreshToken}`)
            request.headers.set('Authorization', `Bearer ${authStore.token}`);
          }
        }
      ],
      // beforeError: [
      //   error => {
      //     if (nuxtApp.$config.public.debug) {
      //       console.error(error);
      //     }
      //     return error;
      //   }
      // ],
      afterResponse: [
        // Si refresh token, on tente de reprendre les rennes
        async (input, options, response) => {
          if (['GET', 'POST', 'DELETE'].includes(input.method) && response.status === 401 && !input.url.match(/\/auth\/refresh$/) && authStore.refreshToken) {
            console.info(`🔄 Auth failed, but refresh token is available`);

            // TODO : mieux gérer l'actualisation du token
            // await refreshAccessTokenWithQueue(authStore.refreshToken);
            await refreshAccessToken(authStore.refreshToken);

            input.headers.set('Authorization', `Bearer ${authStore.token}`);

            return ky(input, options);
          }
          return response;
        },
        // Si page panier, connecté et 401, on se déconnecte sans erreur, avant de chercher un nouveau panier
        async (input, options, response) => {
          if (['GET', 'POST', 'DELETE'].includes(input.method) && response.status === 401 && input.url.match(/\/carts\/my$/) && authStore.user) {
            console.info(`❌ User auth problematic for cart fetching, disconnect, remove auth and retry`);
            // Disconnect
            await clearAuth();
            // Remove auth token
            const reqHeaders = new Headers(options.headers);
            reqHeaders.delete('Authorization');
            options.headers = reqHeaders;
            options.retry.limit = 0;
            return input.method === "GET" ? ky.get(input, options) : ky.post(input, options);
          }
          return response;
        },
        async (request, _options, response) => {
          if (nuxtApp.$config.public.debug) {
            console.info(`⬅️ [${request.method}] ${request.url} [${response.ok ? '✔️' : `❌ ${response.status}`}]`);
          }

          if ([401, 403].includes(response.status)) {
            if (authStore.user) {
              await clearAuth();
            }

            // nuxtApp.runWithContext(() => {
            callWithNuxt(nuxtApp, () => {
              if (!($router as Router)?.currentRoute.value.path.startsWith('/signin')) {
                return navigateTo({
                  path: '/signin',
                  query: { returnUrl: ($router as Router)?.currentRoute.value.fullPath }
                });
              }
            });
          }

          return response;
        }
      ],
    }
  });

  async function signIn(credentials: any) {
    const res = await $apiHttp.post("auth/signin", { json: credentials }).json<{ data: { token?: string, refresh_token?: string, errors?: any } | undefined }>();
    if (res.data && res.data.token) {
      authStore.refreshToken = res.data.refresh_token;
      await signInWithToken(res.data.token);
      authStore.$persist();
    }
    await refreshCart(null);
    return res;
  }

  async function signInWithToken(newToken: string) {
    try {
      authStore.token = newToken;
      await refreshUser();
    } catch (e) {
      authStore.token = undefined;
      authStore.refreshToken = undefined;
    }
  }

  async function refreshUser() {
    try {
      const response = await $apiHttp.get("user/user").json<{ data: UserDto }>();
      authStore.user = response.data;
    } catch (e) {
      authStore.user = undefined;
      throw e;
    }
  }

  async function refreshAccessToken(refreshToken: string) {
    try {
      const response = await $apiHttp.post(`auth/refresh`, { json: { refresh_token: refreshToken } }).json<{ data: { token: string, refresh_token: string } }>();
      authStore.token = response.data.token;
      authStore.refreshToken = response.data.refresh_token;
      // console.debug(`New refresh token: ${response.data.refresh_token}`)
      authStore.$persist();
    } catch (e) {
      authStore.token = undefined;
      authStore.refreshToken = undefined;
      authStore.user = undefined;
      throw e;
    }
  }

  let isRefreshing = false;

  async function refreshAccessTokenWithQueue(refreshToken: string): Promise<void> {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        // Effectue la requête pour rafraîchir le token
        return await refreshAccessToken(refreshToken);
      } catch (error) {
        throw error;
      } finally {
        isRefreshing = false;
      }
    }

    // Attend le nouveau token
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        console.debug("⏳ Waiting for a new token...");
        if (!isRefreshing) {
          console.debug("✔️ New token available");
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
    });
  }

  async function signOut() {
    await $apiHttp.post("auth/signout");
    await clearAuth();
  }

  async function clearAuth() {
    authStore.token = undefined;
    authStore.refreshToken = undefined;
    authStore.user = undefined;
    authStore.$persist();
    await refreshCart(null);
  }

  type ShippingParams = {
    countryCode: string,
    city: string,
    zipCode: string,
  }

  function saveShipping(params: ShippingParams) {
    updateCart({
      // On ré-initialise l'adresse de destination
      shipping_address_id: null,
      shipping_country_code: params.countryCode,
      shipping_city: params.city,
      shipping_zip_code: params.zipCode,
    });
  }

  async function refreshCart(params?: any) {
    const res = await $apiHttp.get(params?.quoteId ? `carts/quote/${params.quoteId}` : `carts/my`, { credentials: 'include' }).json<ApiPdg.R.Cart>();
    if (res.data && !res.data.error) {
      cartStore.cart = res.data.cart;
      cartStore.cartItems = res.data.cartItems;
      cartStore.shipping = {
        countryCode: res.data.cart.shipping_country_code,
        city: res.data.cart.shipping_city,
        zipCode: res.data.cart.shipping_zip_code,
      };
      cartStore.loaded = true;
    }
  }

  async function updateCart(params: any) {
    const res = await $apiHttp.post(params?.quoteId ? `carts/quote/${params.quoteId}` : `carts/my`, { json: { ...params }, credentials: 'include' }).json<ApiPdg.R.Cart>();
    if (res.data && !res.data.error) {
      cartStore.cart = res.data.cart;
      cartStore.cartItems = res.data.cartItems;
      cartStore.shipping = {
        countryCode: res.data.cart.shipping_country_code,
        city: res.data.cart.shipping_city,
        zipCode: res.data.cart.shipping_zip_code,
      };
      cartStore.loaded = true;
    }
    cartStore.$persist();
  }

  async function updateCartItem(params: any) {
    const res = await $apiHttp.post(`carts/my/items`, { json: { ...params }, credentials: 'include' }).json<ApiPdg.R.Cart>();
    if (res.data && !res.data.error) {
      cartStore.cart = res.data.cart;
      cartStore.cartItems = res.data.cartItems;
      cartStore.shipping = {
        countryCode: res.data.cart.shipping_country_code,
        city: res.data.cart.shipping_city,
        zipCode: res.data.cart.shipping_zip_code,
      };
      cartStore.loaded = true;
    }
  }

  return {
    provide: {
      apiHttp: $apiHttp,
      apiHttpUtils: {
        auth: {
          signIn,
          signInWithToken,
          signOut,
          refreshUser,
        },
        cart: {
          saveShipping,
          refresh: refreshCart,
          update: updateCart,
          updateItem: updateCartItem,
        },
      },
    }
  };
});
